import { _ } from "../../core/polyglot/polyglot.js";
import { newDate } from "../../core/helpers/date-util.js";
import Model from "../Model.js";

/**
 * `AbstractIntegration` represents an integration method.
 *
 * @abstract
 * @param {string} spec.companyId - A company identifier
 */
export default class AbstractIntegration extends Model {
	constructor({ companyId, lastImportDate } = {}) {
		super(...arguments);

		this._companyId = companyId;
		this._lastImportDate = lastImportDate;
	}

	getName() {
		return this._subclassResponsibility();
	}

	routeName() {
		return this._subclassResponsibility();
	}

	isFortnoxIntegration() {
		return false;
	}

	isFortnoxExtendedIntegration() {
		return false;
	}

	isEconomicIntegration() {
		return false;
	}

	isPowerOfficeGoIntegration() {
		return false;
	}

	isTripletexIntegration() {
		return false;
	}

	isXledgerIntegration() {
		return false;
	}

	isQuickbooksIntegration() {
		return false;
	}

	isIsaasIntegration() {
		return false;
	}

	isCapegoIntegration() {
		return false;
	}

	// Override this in subclasses whose status is considered
	// stable. We chose to have beta status as default because this is
	// usually the state you want new integrations to be in. Moving to
	// a stable status should be a conscious decision.
	isBeta() {
		return true;
	}

	isUsed() {
		return this._companyId;
	}

	canHaveLinkedDocuments() {
		return false;
	}

	hasInvoiceSupport() {
		return false;
	}

	canBeDisconnected() {
		return false;
	}

	getType() {
		return this.constructor.getType();
	}

	matchType(type) {
		return type === this.getType();
	}

	getLastImportDate() {
		return this._lastImportDate;
	}

	importButtonLabel() {
		return this.importLabel({
			nameRenderer: this.makeButtonLabel.bind(this),
		});
	}

	importLabel({ nameRenderer = this.makeNameLabel.bind(this) } = {}) {
		const betaLabelRenderer = (name) => (html) =>
			html.span(
				name,
				" ",
				html.span({ klass: "label label-info" }, _("Beta")),
			);

		const name = this.getName();

		return nameRenderer(this.isBeta() ? betaLabelRenderer(name) : name);
	}

	makeButtonLabel(name) {
		return _("Import from {name}", { name });
	}

	makeNameLabel(name) {
		return this.makeButtonLabel(name);
	}

	importPath() {
		return `import-${this.routeName()}`;
	}

	importQuery() {
		return {};
	}

	toString() {
		return this.getName();
	}

	importerIsVisible() {
		return true;
	}

	importerCanCreateNewCompany() {
		return true;
	}

	importerCanUpdateExistingCompany() {
		return true;
	}

	static getType() {
		this.subclassResponsibility();
	}

	static transformMaterializationData(data, { companyId }) {
		data.companyId = companyId;

		data.lastImportDate &&= newDate(data.lastImportDate);
	}
}
